import { useCallback, useMemo } from 'react';
import type { PageExperience } from '@/features/experiences/types';
import { isFeedAccessible } from '@/features/sources/lib/feed.lib';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useQueryParams } from '@/hooks/useQueryParams';
import {
  ExperienceTypes,
  type ListExperienceEventInput,
} from '@/types/gql.generated';

export const CATEGORIES_FILTER_PARAM = 'categories';
// legacy Schedule naming for the whoLabels filter
export const WHO_FILTER_PARAM = 'who';
export const LABELS_FILTER_PARAM = 'labels';
// plural, distinct from customize mode's `source` panel param
export const SOURCES_FILTER_PARAM = 'sources';

export const EVENT_FILTER_PARAM_KEYS = [
  CATEGORIES_FILTER_PARAM,
  WHO_FILTER_PARAM,
  LABELS_FILTER_PARAM,
  SOURCES_FILTER_PARAM,
];

export type ExperienceEventFilterIds = {
  categories: string[];
  labels: string[];
  whoLabels: string[];
  sources: string[];
};

type UseExperienceEventFilters = {
  /** Planner-only feature: false hides the UI and ignores any params. */
  isFilteringEnabled: boolean;
  /**
   * Sources still delivering events. One this schedule has lost access to keeps
   * its row in the Sources panel to explain itself, but has nothing to filter by.
   */
  activeFeeds: PageExperience['feeds'];
  filterIds: ExperienceEventFilterIds;
  filteredCategories: PageExperience['categories'];
  filteredLabels: PageExperience['labels'];
  filteredWhoLabels: PageExperience['whoLabels'];
  filteredSources: PageExperience['feeds'];
  hasAppliedFilters: boolean;
  /** Facet fields for the events query; only non-empty facets are set. */
  eventFilterInput: ListExperienceEventInput;
  setFilters: (ids: ExperienceEventFilterIds) => void;
  clearFilters: () => void;
};

/**
 * Adds or removes one id from a facet's selection — the toggle every filter
 * control shares, so the Key's chips and the Filter popover's rows build the
 * same selection from the same rule.
 */
export const toggleFilterId = (ids: string[], id: string): string[] =>
  ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id];

/**
 * Drops every event filter param, restoring the unfiltered view.
 *
 * Split out of useExperienceEventFilters so a consumer that only needs the way
 * back — the out-of-range modal's CTA — doesn't take on that hook's facet
 * resolution, or the feature flag behind it, which needs the current user in
 * context and throws without it.
 */
export const useClearEventFilters = (experienceId: string) => {
  const { removeQueryParam } = useQueryParams();
  const { trackEvent } = useAnalytics();

  return useCallback(() => {
    removeQueryParam(EVENT_FILTER_PARAM_KEYS, { replace: true });
    trackEvent('experience:filter-clear', { experienceId });
  }, [removeQueryParam, trackEvent, experienceId]);
};

const parseIdsParam = (raw: string | null): string[] =>
  raw ? raw.split(',').filter(Boolean) : [];

const toParamValue = (ids: string[]): string | null =>
  ids.length ? ids.join(',') : null;

// Ids in the URL that no longer exist on the experience (deleted label, removed
// feed, or a shared link from another experience) are dropped rather than sent
// to the server, so a stale filter degrades to "off" instead of matching nothing.
// The URL itself is left untouched to keep shared links stable.
const resolve = <T extends { id: string }>(ids: string[], items: T[]): T[] =>
  items.filter((item) => ids.includes(item.id));

export const useExperienceEventFilters = (
  experience: PageExperience
): UseExperienceEventFilters => {
  const { queryParams, setQueryParams } = useQueryParams();
  const { trackEvent } = useAnalytics();

  // Lives on the platform bar, which is planner-only.
  const isFilteringEnabled = experience.type === ExperienceTypes.Planner;

  const rawCategories = queryParams.get(CATEGORIES_FILTER_PARAM);
  const rawWho = queryParams.get(WHO_FILTER_PARAM);
  const rawLabels = queryParams.get(LABELS_FILTER_PARAM);
  const rawSources = queryParams.get(SOURCES_FILTER_PARAM);

  const activeFeeds = useMemo(
    () => experience.feeds.filter(isFeedAccessible),
    [experience.feeds]
  );

  const {
    filterIds,
    filteredCategories,
    filteredLabels,
    filteredWhoLabels,
    filteredSources,
  } = useMemo(() => {
    if (!isFilteringEnabled) {
      return {
        filterIds: { categories: [], labels: [], whoLabels: [], sources: [] },
        filteredCategories: [],
        filteredLabels: [],
        filteredWhoLabels: [],
        filteredSources: [],
      };
    }

    const categories = resolve(
      parseIdsParam(rawCategories),
      experience.categories
    );
    const whoLabels = resolve(parseIdsParam(rawWho), experience.whoLabels);
    const labels = resolve(parseIdsParam(rawLabels), experience.labels);
    // Resolving against the active feeds only, so a revoked source id left in a
    // shared URL degrades to "off" rather than showing as a live selection.
    const sources = resolve(parseIdsParam(rawSources), activeFeeds);

    return {
      filterIds: {
        categories: categories.map(({ id }) => id),
        labels: labels.map(({ id }) => id),
        whoLabels: whoLabels.map(({ id }) => id),
        sources: sources.map(({ id }) => id),
      },
      filteredCategories: categories,
      filteredLabels: labels,
      filteredWhoLabels: whoLabels,
      filteredSources: sources,
    };
  }, [
    isFilteringEnabled,
    rawCategories,
    rawWho,
    rawLabels,
    rawSources,
    experience.categories,
    experience.whoLabels,
    experience.labels,
    activeFeeds,
  ]);

  const hasAppliedFilters =
    filterIds.categories.length > 0 ||
    filterIds.labels.length > 0 ||
    filterIds.whoLabels.length > 0 ||
    filterIds.sources.length > 0;

  const eventFilterInput = useMemo<ListExperienceEventInput>(() => {
    const input: ListExperienceEventInput = {};
    if (filterIds.categories.length) input.categories = filterIds.categories;
    if (filterIds.labels.length) input.labels = filterIds.labels;
    if (filterIds.whoLabels.length) input.whoLabels = filterIds.whoLabels;
    if (filterIds.sources.length) input.sources = filterIds.sources;
    return input;
  }, [filterIds]);

  const setFilters = useCallback(
    (ids: ExperienceEventFilterIds) => {
      setQueryParams(
        {
          [CATEGORIES_FILTER_PARAM]: toParamValue(ids.categories),
          [WHO_FILTER_PARAM]: toParamValue(ids.whoLabels),
          [LABELS_FILTER_PARAM]: toParamValue(ids.labels),
          [SOURCES_FILTER_PARAM]: toParamValue(ids.sources),
        },
        { replace: true }
      );
      trackEvent('experience:filter-update', { experienceId: experience.id });
    },
    [setQueryParams, trackEvent, experience.id]
  );

  const clearFilters = useClearEventFilters(experience.id);

  return {
    isFilteringEnabled,
    activeFeeds,
    filterIds,
    filteredCategories,
    filteredLabels,
    filteredWhoLabels,
    filteredSources,
    hasAppliedFilters,
    eventFilterInput,
    setFilters,
    clearFilters,
  };
};
